import {formatDate, formatNumber} from '@angular/common';
import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import {of, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {AdminDashboardCompanyEngagement, AdminDashboardPoint} from '@features/admin-panel/models/admin-dashboard.models';
import {
  AdminDashboardCompanyUserDetail,
  AdminDashboardSeatsPoint
} from '@features/admin-panel/models/admin-dashboard-c360-engagement.models';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {
  adbCategoryAxis,
  adbChartTheme,
  adbGrid,
  adbLegend,
  adbRedrawOnThemeChange,
  adbTooltip,
  adbValueAxis,
  adbZoom
} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const c360EngagementCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/** The seats chart's ECharts entry point (ITP-E-166-06), stubbed by the spec separately from the logins chart. */
export const c360SeatsCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/**
 * The daily logins as whole weeks (ITP-E-155-05): 7-day chunks counted back from the last day, each dated by its first
 * day. A leading chunk shorter than 7 days is dropped, so no bar is a part week.
 */
export function c360WeeklyLogins(daily: AdminDashboardPoint[]): AdminDashboardPoint[] {
  const points = daily ?? [];
  const weeks: AdminDashboardPoint[] = [];
  for (let end = points.length; end >= 7; end -= 7) {
    const chunk = points.slice(end - 7, end);
    weeks.unshift({date: chunk[0].date, value: chunk.reduce((total, point) => total + (point.value ?? 0), 0)});
  }
  return weeks;
}

/**
 * Company 360 "Engagement" (OCA-E-159 M/company/[id]/page.tsx:390-448): weekly logins with zoom, seats used of
 * licensed seats with active accounts, and the most active users with their last login.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-c360-engagement',
  templateUrl: './admin-dashboard-c360-engagement.component.html',
  styleUrls: ['./admin-dashboard-c360-engagement.component.scss']
})
export class AdminDashboardC360EngagementComponent implements OnChanges, AfterViewInit, OnDestroy {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => {
    this.render();
    this.renderSeats();
  });
  @Input() companyId: string | null = null;
  @ViewChild('chart', {static: true}) chartContainer: ElementRef<HTMLDivElement>;
  @ViewChild('seatsChart', {static: true}) seatsChartContainer: ElementRef<HTMLDivElement>;

  engagement: AdminDashboardCompanyEngagement | null = null;
  /** The daily logins summed into whole weeks (c360WeeklyLogins). */
  weeks: AdminDashboardPoint[] = [];
  /** False until the first response, so the empty state never flashes. */
  loaded = false;

  private chart: echarts.ECharts;
  private seatsChart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService) {}

  /** "126 logins in the last 12 weeks. Zoom to any stretch." */
  get loginsSummary(): string {
    if (!this.weeks.length) {
      return 'No login history yet.';
    }
    const total = this.weeks.reduce((sum, week) => sum + week.value, 0);
    return `${countOf(total, 'login')} in the last ${countOf(this.weeks.length, 'week')}. Zoom to any stretch.`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId']) {
      this.load();
    }
  }

  ngAfterViewInit(): void {
    this.chart = c360EngagementCharts.init(this.chartContainer.nativeElement);
    this.seatsChart = c360SeatsCharts.init(this.seatsChartContainer.nativeElement);
    this.resizeObserver = new ResizeObserver(() => {
      this.chart?.resize();
      this.seatsChart?.resize();
    });
    this.resizeObserver.observe(this.chartContainer.nativeElement);
    this.resizeObserver.observe(this.seatsChartContainer.nativeElement);
    this.render();
    this.renderSeats();
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.chart?.dispose();
    this.chart = null;
    this.seatsChart?.dispose();
    this.seatsChart = null;
  }

  /** Seats used as a share of licensed seats, 0-100, for the seat meter; 0 without licensed seats. */
  seatShare(e: AdminDashboardCompanyEngagement): number {
    return e?.Seats ? Math.min(100, Math.round(e.SeatsUsed / e.Seats * 100)) : 0;
  }

  /** Rows of the users table before "Show all" (OCA-E-159 M/company/[id]/page.tsx:83 USERS_SHOWN). */
  readonly usersShown = 10;

  /** True after "Show all {N users}" until "Show the top 10". */
  allUsers = false;

  /** The clock "Today" / "N days ago" count from; a spec pins it. */
  nowMs: () => number = () => Date.now();

  /** True when the API sent `Users` (ITP-E-166-05): the table lists every user; otherwise the R4 top-users table. */
  get hasUsers(): boolean {
    return Array.isArray(this.engagement?.Users);
  }

  /** The rows on screen: the first 10, or all of them after "Show all". The API already sorts them. */
  get shownUsers(): AdminDashboardCompanyUserDetail[] {
    const users = this.engagement?.Users ?? [];
    return this.allUsers ? users : users.slice(0, this.usersShown);
  }

  /** "24 users, most active first: …" (M/company/[id]/page.tsx:402); without `Users` the R4 sentence. */
  get usersSubtitle(): string {
    return this.hasUsers
      ? `${countOf(this.engagement.Users.length, 'user')}, most active first: logins in the last 30 days and when each was last active.`
      : 'The most active users in the last 30 days, with their last login.';
  }

  /** "Show all 24 users" / "Show the top 10". */
  get usersToggleText(): string {
    return this.allUsers ? `Show the top ${this.usersShown}` : `Show all ${countOf(this.engagement?.Users?.length ?? 0, 'user')}`;
  }

  /** Whole UTC days since the user was last active (0 = today); null when never. */
  daysSinceActive(user: AdminDashboardCompanyUserDetail): number | null {
    if (!user?.lastActive) {
      return null;
    }
    const day = (ms: number): number => Math.floor(ms / 86400000);
    return Math.max(0, day(this.nowMs()) - day(Date.parse(user.lastActive)));
  }

  /** "Never", "Today", "1 day ago", "33 days ago". */
  lastActiveText(user: AdminDashboardCompanyUserDetail): string {
    const days = this.daysSinceActive(user);
    return days === null ? 'Never' : days === 0 ? 'Today' : `${countOf(days, 'day')} ago`;
  }

  /** Last active more than 60 days ago: the cell turns amber. */
  isStale(user: AdminDashboardCompanyUserDetail): boolean {
    const days = this.daysSinceActive(user);
    return days !== null && days > 60;
  }

  /** True when the API sent `SeatsSeries` (ITP-E-166-05): the seats card draws the 12-window chart. */
  get hasSeatsSeries(): boolean {
    return Array.isArray(this.engagement?.SeatsSeries);
  }

  /** The 12 trailing 30-day windows, oldest first (empty without `SeatsSeries`). */
  get seatsSeries(): AdminDashboardSeatsPoint[] {
    return this.engagement?.SeatsSeries ?? [];
  }

  /** No window has a figure yet: the card says it is collecting history instead of drawing an empty chart. */
  get seatsCollecting(): boolean {
    return this.seatsSeries.every(point => (point.activeUsers ?? null) === null && (point.seats ?? null) === null);
  }

  /** "7 of 10 seats used in the last 30 days."; without `SeatsSeries` the R4 sentence. */
  get seatsSubtitle(): string {
    if (!this.hasSeatsSeries) {
      return 'Distinct users in the last 30 days, against licensed seats.';
    }
    return `${formatNumber(this.engagement?.SeatsUsed ?? 0, 'en-US', '1.0-0')} of ${countOf(this.engagement?.Seats ?? 0, 'seat')} used in the last 30 days.`;
  }

  /**
   * "<b>30 days to 3 Oct</b><br/>● Active users: <b>7</b><br/>● Licensed seats: <b>10</b>" (OCA-E-159 M/charts.ts:780-791),
   * each dot in its series colour; a missing figure reads "–".
   */
  seatsTooltipText(index: number): string {
    const point = this.seatsSeries[index];
    if (!point) {
      return '';
    }
    const t = adbChartTheme(this.seatsChartContainer?.nativeElement);
    const figure = (value: number | null): string =>
      value === null || value === undefined ? '–' : formatNumber(value, 'en-US', '1.0-0');
    return `<b>30 days to ${formatDate(point.end, 'd MMM', 'en-US', 'UTC')}</b>`
      + `<br/><span style="color:${t.greenBar}">●</span> Active users: <b>${figure(point.activeUsers)}</b>`
      + `<br/><span style="color:${t.ink2}">●</span> Licensed seats: <b>${figure(point.seats)}</b>`;
  }

  /** "<b>Week of 18 Sep</b><br/>28 logins". */
  tooltipText(index: number): string {
    const week = this.weeks[index];
    return week ? `<b>Week of ${formatDate(week.date, 'd MMM', 'en-US', 'UTC')}</b><br/>${countOf(week.value, 'login')}` : '';
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    this.loaded = false;
    this.allUsers = false;
    if (!this.companyId) {
      return;
    }
    this.loadSubscription = this.dataService.getCompanyEngagement(this.companyId).pipe(
      catchError(() => of(null as AdminDashboardCompanyEngagement))
    ).subscribe(engagement => {
      this.engagement = engagement;
      this.weeks = c360WeeklyLogins(engagement?.DailyLogins ?? []);
      this.loaded = true;
      this.render();
      this.renderSeats();
    });
  }

  private render(): void {
    if (!this.chart) {
      return;
    }
    const t = adbChartTheme(this.chartContainer.nativeElement);
    const option: echarts.EChartsOption = {
      textStyle: {fontFamily: t.font},
      grid: adbGrid({bottom: 52}),
      legend: {...adbLegend(t), data: ['Logins']},
      xAxis: adbCategoryAxis(t, this.weeks.map(week => formatDate(week.date, 'd MMM', 'en-US', 'UTC'))),
      yAxis: adbValueAxis(t, value => formatNumber(value, 'en-US', '1.0-0'), {minInterval: 1}),
      dataZoom: adbZoom(t),
      tooltip: adbTooltip(t, {
        axisPointer: {type: 'shadow'},
        formatter: (params: any) => this.tooltipText((Array.isArray(params) ? params[0] : params)?.dataIndex ?? 0)
      }),
      series: [{
        type: 'bar',
        name: 'Logins',
        data: this.weeks.map(week => week.value),
        barMaxWidth: 16,
        itemStyle: {color: t.blue2, borderRadius: [2, 2, 0, 0]}
      }]
    };
    this.chart.setOption(option, true);
  }

  /** Active users as green bars, licensed seats as a dashed muted line, one point per 30-day window (ITP-E-166-06). */
  private renderSeats(): void {
    if (!this.seatsChart) {
      return;
    }
    const t = adbChartTheme(this.seatsChartContainer.nativeElement);
    const points = this.seatsSeries;
    const option: echarts.EChartsOption = {
      textStyle: {fontFamily: t.font},
      grid: adbGrid(),
      legend: {...adbLegend(t), data: ['Active users', 'Licensed seats']},
      xAxis: adbCategoryAxis(t, points.map(point => formatDate(point.end, 'd MMM', 'en-US', 'UTC'))),
      yAxis: adbValueAxis(t, value => formatNumber(value, 'en-US', '1.0-0'), {minInterval: 1}),
      tooltip: adbTooltip(t, {
        axisPointer: {type: 'shadow'},
        formatter: (params: any) => this.seatsTooltipText((Array.isArray(params) ? params[0] : params)?.dataIndex ?? 0)
      }),
      series: [
        {
          type: 'bar',
          name: 'Active users',
          data: points.map(point => point.activeUsers ?? null),
          barMaxWidth: 16,
          itemStyle: {color: t.greenBar, borderRadius: [2, 2, 0, 0]}
        },
        {
          type: 'line',
          name: 'Licensed seats',
          data: points.map(point => point.seats ?? null),
          showSymbol: false,
          connectNulls: true,
          lineStyle: {width: 2, color: t.ink2, type: 'dashed'},
          itemStyle: {color: t.ink2}
        }
      ]
    };
    this.seatsChart.setOption(option, true);
  }
}
